import React, { useState } from 'react';
import { Plus, Pencil, Trash2, Copy, BookmarkPlus, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { fmtMoney } from '@/lib/officialsApi';
import { Panel } from './PayBits';

const TemplateRow = ({ t, roles, currency, events, can, onApply, onEdit, onDelete }) => {
  const [confirm, setConfirm] = useState(false);
  const label = (k) => roles.find(r => r.key === k)?.label || k;
  const src = events.find(e => e.id === t.source_event_id)?.name;
  return (
    <div className="px-4 py-3 border-t first:border-t-0" data-testid={`template-row-${t.id}`}>
      <div className="flex flex-wrap items-center gap-2">
        <div className="flex-1 min-w-[160px]">
          <div className="font-bold text-slate-900" data-testid={`template-name-${t.id}`}>{t.name}</div>
          <div className="text-[11px] text-slate-400">{src ? `From ${src} · ` : ''}by {t.updated_by}</div>
        </div>
        {can && !confirm && (
          <div className="flex items-center gap-1">
            <Button size="sm" onClick={() => onApply(t)} className="h-8 bg-[#00247D] hover:bg-[#001A5C]" data-testid={`template-apply-${t.id}`}><Copy className="w-3.5 h-3.5 mr-1.5" />Apply</Button>
            <Button size="sm" variant="ghost" className="h-8 px-2" onClick={() => onEdit(t)} data-testid={`template-edit-${t.id}`}><Pencil className="w-3.5 h-3.5" /></Button>
            <Button size="sm" variant="ghost" className="h-8 px-2 text-slate-400 hover:text-red-600" onClick={() => setConfirm(true)} data-testid={`template-delete-${t.id}`}><Trash2 className="w-3.5 h-3.5" /></Button>
          </div>
        )}
        {confirm && (
          <span className="flex items-center gap-2"><span className="text-xs text-slate-500">Leagues keep their fees.</span>
            <Button size="sm" variant="outline" className="h-8" onClick={() => setConfirm(false)}>Keep</Button>
            <Button size="sm" className="h-8 bg-red-600 hover:bg-red-700" onClick={() => onDelete(t)} data-testid={`template-delete-confirm-${t.id}`}>Delete</Button></span>
        )}
      </div>
      <div className="flex flex-wrap gap-1.5 mt-2">
        {Object.entries(t.rates).map(([k, m]) => (
          <span key={k} className="text-[11px] bg-slate-100 text-slate-700 rounded-full px-2 py-0.5"><b>{label(k)}</b> {fmtMoney(m, currency)}</span>
        ))}
        {t.budget_minor !== null && <span className="text-[11px] bg-emerald-50 text-emerald-800 rounded-full px-2 py-0.5">Budget {fmtMoney(t.budget_minor, currency)}</span>}
      </div>
    </div>
  );
};

export const TemplatesCard = ({ templates, roles, currency, events, can, onNew, onApply, onEdit, onDelete }) => (
  <Panel title="Rate templates" desc="Save a fee setup once, then apply it to new seasons and tournaments." testId="rate-templates"
    actions={can && <Button size="sm" variant="outline" onClick={onNew} data-testid="template-new-btn"><Plus className="w-4 h-4 mr-1.5" />New template</Button>}>
    {templates.map(t => <TemplateRow key={t.id} t={t} roles={roles} currency={currency} events={events} can={can} onApply={onApply} onEdit={onEdit} onDelete={onDelete} />)}
    {!templates.length && (
      <div className="py-8 px-4 text-center" data-testid="templates-empty">
        <BookmarkPlus className="w-7 h-7 mx-auto text-slate-300" />
        <p className="mt-2 text-sm text-slate-500">No templates yet. Pick a league in the editor and tap "Save as template", or start a new one.</p>
      </div>
    )}
  </Panel>
);

export const NoFeesBanner = ({ events, can, onCopy }) => {
  const [all, setAll] = useState(false);
  if (!events.length) return null;
  const shown = all ? events : events.slice(0, 6);
  return (
    <div className="rounded-2xl border border-amber-200 bg-amber-50/60 px-4 py-3" data-testid="no-fees-banner">
      <div className="flex flex-wrap items-center gap-2">
        <Sparkles className="w-4 h-4 text-amber-600" />
        <p className="text-sm text-amber-900 mr-auto"><b>{events.length} league{events.length === 1 ? '' : 's'}</b> {events.length === 1 ? "doesn't" : "don't"} have their own fees yet and use the default rates.</p>
        {can && events.length > 1 && <Button size="sm" variant="outline" className="h-8 bg-white" onClick={() => onCopy(events.map(e => e.id))} data-testid="no-fees-copy-all">Copy to all {events.length}…</Button>}
      </div>
      <div className="flex flex-wrap gap-2 mt-2.5">
        {shown.map(e => (
          <span key={e.id} className="inline-flex max-w-full items-center gap-2 bg-white border border-amber-200 rounded-full pl-3 pr-1 py-1 text-sm" data-testid={`no-fees-${e.id}`}>
            <span className="font-medium text-slate-800 truncate">{e.name}</span>
            {can && <button type="button" onClick={() => onCopy([e.id])} className="shrink-0 whitespace-nowrap text-xs font-semibold text-[#00247D] bg-[#00247D]/5 hover:bg-[#00247D]/10 rounded-full px-2.5 py-1 transition-colors" data-testid={`no-fees-copy-${e.id}`}>Copy from…</button>}
          </span>
        ))}
        {events.length > 6 && <button type="button" onClick={() => setAll(a => !a)} className="text-xs font-semibold text-amber-800 underline">{all ? 'Show less' : `+${events.length - 6} more`}</button>}
      </div>
    </div>
  );
};
